import React, { useState, useEffect, useCallback, useContext } from 'react';
import AuthContext from '../context/AuthContext';
import Modal from '../components/Modal';
import EbookForm from '../components/EbookForm';
import PdfViewerModal from '../components/PdfViewerModal';
import Pagination from '../components/Pagination';
import api from '../api/axiosConfig';
import { FaEye, FaDownload } from 'react-icons/fa';
import { showSuccessToast, showErrorAlert, showConfirmDialog } from '../utils/alert';
import './ServicePage.css';

const EbookPage = () => {
  const { user } = useContext(AuthContext);
  const [ebookList, setEbookList] = useState([]);
  const [isFormOpen, setFormOpen] = useState(false);
  const [isViewerOpen, setViewerOpen] = useState(false);
  const [fileToView, setFileToView] = useState('');
  const [currentData, setCurrentData] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const API_URL = '/api/ebook';

  const fetchData = useCallback(async (page = 1, limit = itemsPerPage, keyword = debouncedSearchTerm) => {
    try {
      const response = await api.get(`${API_URL}?keyword=${keyword}&page=${page}&limit=${limit}`);
      setEbookList(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error) {
      console.error("Gagal mengambil data E-Book:", error);
      showErrorAlert("Gagal memuat data E-Book.");
    }
  }, [itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    const timerId = setTimeout(() => { setDebouncedSearchTerm(searchTerm); setCurrentPage(1); }, 500);
    return () => clearTimeout(timerId);
  }, [searchTerm]);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [debouncedSearchTerm, currentPage, itemsPerPage, fetchData]);

  const handlePageChange = (pageNumber) => { fetchData(pageNumber); };
  const handleItemsPerPageChange = (limit) => { setItemsPerPage(limit); fetchData(1, limit); };
  
  const handleSave = async (formData) => {
    const isEditing = !!currentData;
    const url = isEditing ? `${API_URL}/${currentData._id}` : API_URL;
    const method = isEditing ? 'put' : 'post';
    try {
      await api[method](url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormOpen(false);
      fetchData(currentPage);
      showSuccessToast(isEditing ? 'E-Book berhasil diperbarui!' : 'E-Book berhasil ditambahkan!');
    } catch (error) {
      console.error("Gagal menyimpan data:", error);
      showErrorAlert("Gagal menyimpan data E-Book");
    }
  };

  const handleDelete = async (id) => {
    const result = await showConfirmDialog();
    if (result.isConfirmed) {
      try {
        await api.delete(`${API_URL}/${id}`);
        fetchData(currentPage);
        showSuccessToast('E-Book berhasil dihapus!');
      } catch (error) {
        console.error("Gagal menghapus data:", error);
        showErrorAlert("Gagal menghapus data E-Book.");
      }
    }
  };
  
  const openFormModal = (data = null) => {
    setCurrentData(data);
    setFormOpen(true);
  };
  
  const openViewerModal = (file) => {
    setFileToView(file);
    setViewerOpen(true);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <p>Master Data / E-Book</p>
        <h1>E-Book</h1>
      </div>
      <div className="content-card">
        <div className="toolbar">
          {(user.role === 'superadmin') && (
            <button className="btn-primary" onClick={() => openFormModal()}>+ Tambah E-Book</button>
          )}
          <input 
            type="text"
            placeholder="Cari Deskripsi..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginLeft: 'auto' }}
          />
        </div>
        <div className="table-responsive-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Deskripsi</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {ebookList?.map((item, index) => (
                <tr key={item._id}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>{item.deskripsi}</td>
                  <td className="action-buttons">
                    {item.file && (
                      <>
                        <button onClick={() => openViewerModal(item.file)} title="Lihat E-Book">
                          <FaEye />
                        </button>
                        
                        <a
                          href={`${process.env.REACT_APP_API_URL}/uploads/${item.file}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Unduh dan Lihat E-Book"
                        >
                          <FaDownload />
                        </a>
                      </>

                    )}
                    {(user.role === 'superadmin') && (
                      <button onClick={() => openFormModal(item)} title="Edit">✏️</button>
                    )}
                    {(user.role === 'superadmin') && (
                      <button onClick={() => handleDelete(item._id)} title="Hapus">🗑️</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
        
      </div>
      <Modal show={isFormOpen} onClose={() => setFormOpen(false)} title={currentData ? "Edit E-Book" : "Tambah E-Book"}>
        <EbookForm existingData={currentData} onSave={handleSave} onCancel={() => setFormOpen(false)} />
      </Modal>
      <PdfViewerModal 
        show={isViewerOpen} 
        onClose={() => setViewerOpen(false)} 
        fileUrl={`${process.env.REACT_APP_API_URL}/uploads/${fileToView}`} 
        size="large"
        title="E-Book"
      />
    </div>
  );
};

export default EbookPage;